/**
 * <s-cart-peek> — mini cart that drops down from the header cart icon.
 * Opens on the `s-cart-peek:open` document event (fired after swipe-to-cart adds),
 * lines up under the cart icon and closes itself after a few seconds unless the
 * shopper is hovering or interacting with it. Light-dismiss via the popover API.
 */
const AUTO_CLOSE_MS = 8000;

class SCartPeek extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    this.panel = this.querySelector('[popover]');
    if (!this.panel) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    document.addEventListener('s-cart-peek:open', () => this.open(), opts);
    window.addEventListener('resize', () => this.#position(), opts);
    for (const type of ['pointerenter', 'focusin', 'touchstart']) {
      this.panel.addEventListener(type, () => clearTimeout(this.#timer), { ...opts, passive: true });
    }
    this.panel.addEventListener('pointerleave', () => this.#scheduleClose(), opts);
    this.panel.addEventListener('toggle', (event) => {
      if (event.newState === 'closed') clearTimeout(this.#timer);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  open() {
    if (!this.panel) return;
    this.#position();
    if (!this.panel.matches(':popover-open')) {
      try { this.panel.showPopover(); } catch { return; }
    }
    this.#scheduleClose();
  }

  #scheduleClose() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      if (this.panel.matches(':popover-open')) this.panel.hidePopover();
    }, AUTO_CLOSE_MS);
  }

  #position() {
    const trigger = document.getElementById('header-cart-trigger');
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const top = Math.max(8, rect.bottom + 10);
    const fromEnd = Math.max(12, window.innerWidth - rect.right - 4);
    this.panel.style.top = `${top}px`;
    this.panel.style.insetInlineEnd = `${fromEnd}px`;
    // Point the notch at the icon.
    const notch = this.panel.querySelector('[aria-hidden="true"]');
    if (notch) notch.style.insetInlineEnd = `${Math.max(12, rect.width / 2 - 2)}px`;
  }
}

if (!customElements.get('s-cart-peek')) customElements.define('s-cart-peek', SCartPeek);
